import { Component, ViewEncapsulation, inject, ChangeDetectionStrategy } from '@angular/core';
import { OrdersNavMenuComponent } from '../orders/orders-nav-menu.component';
import { PreTradeCheckNavMenuComponent } from '../compliance/pre-trade-check/pre-trade-check-nav-menu.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrderFlowAuthService } from '../auth/order-flow-auth.service';
import { SidebarStateService } from './sidebar-state.service';
import { ReportsNavMenuComponent } from './reports-nav-menu.component';
import { RoleGateComponent } from '../shared/authz/role-gate.component';
import { ROLE_GROUPS } from '../shared/authz/role-groups';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SidebarTooltipDirective } from '../shell/sidebar-tooltip.directive';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'of-sidebar',
  encapsulation: ViewEncapsulation.None,
  imports: [
    OrdersNavMenuComponent,
    PreTradeCheckNavMenuComponent,
    RoleGateComponent,
    ReportsNavMenuComponent,
    RouterLink,
    RouterLinkActive,
    SidebarTooltipDirective
  ],
  template: `@if (loggedIn()) {
    <aside id="sidebar" class="sidebar-shell collapse d-lg-block" [class.expanded]="sidebarState.expanded()">
      <div class="sidebar-panel">
        <div class="sidebar-toggle-row">
          <button
            class="sidebar-toggle-btn"
            (click)="sidebarState.toggle()"
            [title]="sidebarState.expanded() ? 'Collapse sidebar' : 'Expand sidebar'"
          >
            <i
              class="bi"
              [class.bi-arrow-bar-left]="sidebarState.expanded()"
              [class.bi-arrow-bar-right]="!sidebarState.expanded()"
            ></i>
          </button>
        </div>
        <div class="sidebar-section sidebar-section--primary">
          <div class="sidebar-header">
            <h6 class="sidebar-heading">Orders</h6>
          </div>
          <of-orders-nav-menu></of-orders-nav-menu>
        </div>
        <div class="sidebar-section mb-1">
          <div class="sidebar-header">
            <h6 class="sidebar-heading">Counterparty Info</h6>
          </div>
          <of-role-gate [includeRoles]="roleGroups.brokerView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/brokers']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="Brokers"
              >
                <i class="bi bi-building me-2"></i>
                <span>Brokers</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.custodianView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/custodians']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="Custodians"
              >
                <i class="bi bi-bank me-2"></i>
                <span>Custodians</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.fiduciaryView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/fiduciaries']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="Fiduciaries"
              >
                <i class="bi bi-person-vcard me-2"></i>
                <span>Fiduciaries</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.tdRatesView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/td-rates']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="TD Rates"
              >
                <i class="bi bi-percent me-2"></i>
                <span>TD Rates</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.feeView">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/fees']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="Fee Schedule"
              >
                <i class="bi bi-cash-coin me-2"></i>
                <span>Fee Schedule</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.fundamentalData">
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/securities']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="Securities"
              >
                <i class="bi bi-graph-up me-2"></i>
                <span>Securities</span>
              </a>
            </div>
            <div id="sidebar-nav" class="list-group text-sm-start">
              <a
                class="list-group-item border-0 mb-1 p-1"
                [routerLink]="['/portfolios']"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ exact: false }"
                ofSidebarTooltip="Portfolios"
              >
                <i class="bi bi-briefcase me-2"></i>
                <span>Portfolios</span>
              </a>
            </div>
          </of-role-gate>
          <of-role-gate [includeRoles]="roleGroups.orderReadWrite"
            ><of-pre-trade-check-nav-menu></of-pre-trade-check-nav-menu
          ></of-role-gate>
        </div>

        <div class="sidebar-section sidebar-section--utility">
          <of-reports-nav-menu></of-reports-nav-menu>
        </div>
      </div>
    </aside>
  }`,
  styleUrls: ['./sidebar.scss']
})
export class SidebarComponent {
  private readonly orderFlowAuthService = inject(OrderFlowAuthService);
  protected readonly roleGroups = ROLE_GROUPS;
  protected readonly loggedIn = toSignal(this.orderFlowAuthService.loggedIn$, { initialValue: false });
  protected readonly sidebarState = inject(SidebarStateService);
}
